import React, { useContext, useEffect } from 'react';

import { DataTable } from '@uhg-abyss/web/ui/DataTable';
import { useDataTable } from '@uhg-abyss/web/hooks/useDataTable';

import { Link } from '@uhg-abyss/web/ui/Link';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Box } from '@uhg-abyss/web/ui/Box';
import { getServiceURL } from '@src/env.config';
import { Card } from '@uhg-abyss/web/ui/Card';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useLazyGetReportsQuery } from '@src/store/services/cdb/onlineServices';
import { useLazyGetGpsErrorAnalysisRptQuery } from '@src/store/services/gps/onlineServices';
import { mapV1ColumnsToV2, withRowIds } from '@src/utils/tableUtils';
import { HEADER_THEME_CSS } from '@src/common/Table';
import { FileContext } from '@src/routes/Routes';

export const ErrorWarningSummary = ({ fileId, setId, isChild }) => {
  const router = useRouter();
  const { service } = router.getRouteParams();

  const { fileData } = useContext(FileContext);
  const isThresholdExceeded =
    fileData.fileStatus.includes('THRESHOLD_EXCEEDED');
  const reportTypeSubCor = 'IE';
  const reportTypeElig = 'EE';
  const [trigger, { data: errorWarningReports, isFetching, isError }] =
    service === 'gps'
      ? useLazyGetGpsErrorAnalysisRptQuery()
      : useLazyGetReportsQuery();

  const reqData = router.getRouteParams();

  useEffect(() => {
    trigger({
      /*  page: fileData?.page,
      pageSize: fileData?.pageSize,
      count: !fileData?.count, */
      reportType: reqData?.reportType || '',
      fileId: fileData?.fileId || reqData?.fileId || '',
      transSetId: fileData?.transSetId || '0',
      // totalCount: fileData?.count || '0',
      allDate: fileData?.allDate || '',
      totalErrors: fileData?.totalErrors || 0,
      errorsShown: fileData?.errorsShown || 0,
      fieldCode: fileData?.fieldCode,
      sbmtGrpId: fileData?.sbmtGrpId || '',
      processingDt: fileData?.processingDt,
      fileRecvDate: fileData?.fileRecvDate,
      fileInd: '1',
      sbmtGrpNm: fileData?.sbmtGrpId || '',
    });
  }, [setId, fileData?.transSetId]);

  const NameLink = ({ name }) => {
    const url = `${getServiceURL()}/home/help/ENW`;

    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: 'white' }}
      >
        {name}
      </a>
    );
  };

  const submitterColumns = React.useMemo(() => {
    return [
      {
        Header: 'Severity',
        accessor: 'severityCode',
        canToggleVisibilty: false,
        isHiddenByDefault: false,
        Cell: ({ row, value }) => {
          const backgroundColor =
            row.original.updated === true ? '#FAFAA7' : '';
          return (
            <Box color={backgroundColor}>
              <strong>{value}</strong>
            </Box>
          );
        },
      },
      {
        Header: <NameLink name="Error and Warning Name" />,
        accessor: 'errorOrWarningName',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        label: 'Customer Name',
        Cell: ({ row, value }) => {
          const backgroundColor =
            row.original.updated === true ? '#FAFAA7' : '';

          return (
            <Box color={backgroundColor}>
              {isThresholdExceeded ? (
                <strong>{value}</strong>
              ) : (
                <Link
                  href={`${service === 'gps' ? '/gps' : ''}/home/${
                    isChild ? 'child-reports' : 'reports'
                  }/${setId}${
                    isChild ? `/${setId}` : ''
                  }/${reportTypeSubCor}/${row.original.errorOrWarningName.replace(
                    /[\\/]/g,
                    ' '
                  )}/${
                    service === 'gps'
                      ? row.original.errTypCd
                      : row.original.errorCode
                  }/${backgroundColor}`}
                >
                  {value}
                </Link>
              )}
            </Box>
          );
        },
      },
      {
        Header: 'Count',
        accessor: 'count',
        canToggleVisibilty: false,
        isHiddenByDefault: false,
        Cell: ({ row, value }) => {
          const backgroundColor =
            row.original.updated === true ? '#FAFAA7' : '';
          return (
            <Box color={backgroundColor}>
              <strong>{value}</strong>
            </Box>
          );
        },
        Footer: (info) => {
          const total = React.useMemo(() => {
            return info.rows.reduce((sum, row) => {
              return row.values.count + sum;
            }, 0);
          }, [info.rows]);
          return (
            <Text css={{ color: '$web.semantic.color.text.content.primary' }}>
              {total}
            </Text>
          );
        },
      },
    ];
  }, [fileId]);

  const elegibilityColumns = React.useMemo(() => {
    return [
      {
        Header: 'Severity',
        accessor: 'severityCode',
        canToggleVisibilty: false,
        isHiddenByDefault: false,
        Cell: ({ row, value }) => {
          const backgroundColor =
            row.original.updated === true ? '#FAFAA7' : '';

          return (
            <Box color={backgroundColor}>
              <strong>{value}</strong>
            </Box>
          );
        },
      },
      {
        Header: <NameLink name="Error and Warning Name" />,
        accessor: 'errorOrWarningName',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        label: 'Customer Name',
        Cell: ({ row, value }) => {
          const backgroundColor =
            row.original.updated === true ? '#FAFAA7' : '';
          return (
            <Box color={backgroundColor}>
              {isThresholdExceeded ? (
                <strong>{value}</strong>
              ) : (
                <Link
                  href={`${service === 'gps' ? '/gps' : ''}/home/${
                    isChild ? 'child-reports' : 'reports'
                  }/${setId}${
                    isChild ? `/${setId}` : ''
                  }/${reportTypeElig}${`/${row.original.errorOrWarningName.replace(
                    /[\\/]/g,
                    ''
                  )}`}/${
                    service === 'gps'
                      ? row.original.errTypCd
                      : row.original.errorCode
                  }/${backgroundColor}`}
                >
                  {value}
                </Link>
              )}
            </Box>
          );
        },
      },
      {
        Header: 'Count',
        accessor: 'count',
        canToggleVisibilty: false,
        isHiddenByDefault: false,
        Cell: ({ row, value }) => {
          const backgroundColor =
            row.original.updated === true ? '#FAFAA7' : '';
          return (
            <Box color={backgroundColor}>
              <strong>{value}</strong>
            </Box>
          );
        },
        Footer: (info) => {
          const total = React.useMemo(() => {
            return info.rows.reduce((sum, row) => {
              return row.values.count + sum;
            }, 0);
          }, [info.rows]);
          return (
            <Text css={{ color: '$web.semantic.color.text.content.primary' }}>
              {total}
            </Text>
          );
        },
      },
    ];
  }, [fileId]);

  const custColumnsV2 = React.useMemo(
    () => mapV1ColumnsToV2(elegibilityColumns),
    [elegibilityColumns]
  );
  const subColumnsV2 = React.useMemo(
    () => mapV1ColumnsToV2(submitterColumns),
    [submitterColumns]
  );

  const dataTablePropsCustENW = useDataTable<any>({
    initialData: [] as any[],
    initialColumns: custColumnsV2,
    rowIdKey: 'uniqueId',
    paginationConfig: { enablePagination: false },
  });

  const dataTablePropSubENW = useDataTable<any>({
    initialData: [] as any[],
    initialColumns: subColumnsV2,
    rowIdKey: 'uniqueId',
    paginationConfig: { enablePagination: false },
  });

  useEffect(() => {
    if (custColumnsV2?.length) {
      dataTablePropsCustENW.setColumns(custColumnsV2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [custColumnsV2]);

  useEffect(() => {
    if (subColumnsV2?.length) {
      dataTablePropSubENW.setColumns(subColumnsV2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subColumnsV2]);

  useEffect(() => {
    if (errorWarningReports) {
      const custCorrectionsENW =
        errorWarningReports.reportData?.filter((item) => {
          return item.categoryCode.trim() === 'E';
        }) ?? [];

      const eligAnalystENW =
        errorWarningReports.reportData?.filter((item) => {
          return item.categoryCode.trim() === 'I';
        }) ?? [];

      dataTablePropsCustENW.setData(withRowIds(custCorrectionsENW));
      dataTablePropSubENW.setData(withRowIds(eligAnalystENW));
    }
  }, [errorWarningReports]);

  return (
    <Card
      css={{
        'abyss-card-root': { width: '100%', padding: '0', borderRadius: '4px' },
      }}
    >
      <div
        style={{
          backgroundColor:
            'var(--abyss-colors-web-semantic-color-surface-container-primary)',
          color:
            'var(--abyss-colors-web-semantic-color-text-content-primaryAlt)',
          paddingTop: '5px',
          paddingBottom: '5px',
          paddingLeft: '24px',
          paddingRight: '24px',
          borderTopLeftRadius: '3px',
          borderTopRightRadius: '3px',
          fontSize: '18px',
          fontWeight: 'bold',
        }}
      >
        Error &amp; Warning Summary
      </div>
      <div style={{ padding: '16px' }}>
        <Card
          css={{
            'abyss-card-root': {
              width: '100%',
              padding: '0',
              borderRadius: '4px',
            },
          }}
        >
          <div
            style={{
              backgroundColor:
                'var(--abyss-colors-web-semantic-color-surface-container-primary)',
              color:
                'var(--abyss-colors-web-semantic-color-text-content-primaryAlt)',
              paddingTop: '5px',
              paddingBottom: '5px',
              paddingLeft: '24px',
              paddingRight: '24px',
              borderTopLeftRadius: '3px',
              borderTopRightRadius: '3px',
              fontSize: '18px',
              fontWeight: 'bold',
            }}
          >
            Customer Corrections Required
          </div>
          <div
            style={{
              paddingTop: '16px',
              paddingBottom: '16px',
              paddingLeft: '24px',
              paddingRight: '24px',
              fontSize: '16px',
              fontWeight: '500',
              display: 'block',
            }}
          >
            <DataTable hideHeader title="" tableState={dataTablePropsCustENW}>
              <DataTable.Table
                css={{
                  width: '100%',
                  '.abyss-data-table-cell': { padding: 0 },
                  'abyss-data-table-base-cell': { flex: '1 0 auto' },
                  'abyss-data-table-header-data': {
                    fontSize: '14px !important',
                  },
                  ...HEADER_THEME_CSS,
                }}
              />
            </DataTable>
          </div>
        </Card>
      </div>
      <div style={{ padding: '16px' }}>
        <Card
          css={{
            'abyss-card-root': {
              width: '100%',
              padding: '0',
              borderRadius: '4px',
            },
          }}
        >
          <div
            style={{
              backgroundColor:
                'var(--abyss-colors-web-semantic-color-surface-container-primary)',
              color:
                'var(--abyss-colors-web-semantic-color-text-content-primaryAlt)',
              paddingTop: '5px',
              paddingBottom: '5px',
              paddingLeft: '24px',
              paddingRight: '24px',
              borderTopLeftRadius: '3px',
              borderTopRightRadius: '3px',
              fontSize: '18px',
              fontWeight: 'bold',
            }}
          >
            Eligibility Analyst Corrections Required
          </div>
          <div
            style={{
              paddingTop: '16px',
              paddingBottom: '16px',
              paddingLeft: '24px',
              paddingRight: '24px',
              fontSize: '16px',
              fontWeight: '500',
              display: 'block',
            }}
          >
            <DataTable hideHeader title="" tableState={dataTablePropSubENW}>
              <DataTable.Table
                css={{
                  width: '100%',
                  'abyss-data-table-cell': { padding: 0 },
                  'abyss-data-table-base-cell': { flex: '1 0 auto' },
                  'abyss-data-table-header-data': {
                    fontSize: '14px !important',
                  },
                  ...HEADER_THEME_CSS,
                }}
              />
            </DataTable>
          </div>
        </Card>
      </div>
    </Card>
  );
};
